import type {
  Auth,
  IdTokenResult,
  User,
} from 'firebase/auth'
import {
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
} from 'firebase/auth'

// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
let userLoggedIn = Promise.withResolvers<void>()
let firebaseAuth: Auth

export function useAuth() {
  const isLoggedIn = computed(() => !!user.value)
  // markRaw: a reactive proxy around Firebase internals reaches the auth
  // popup/iframe windows; Vue then reads `__v_isRef` on a cross-origin window
  // and throws (seen after logging in inside the Apro iframe).
  const user = useState<User | null>('user', () => null)
  const idToken = useState<IdTokenResult | null>('idToken', () => null)

  /**
   * Initialize the auth state
   */
  function initialize() {
    if (firebaseAuth) return

    firebaseAuth = getAuth(useFirebaseApp())

    onAuthStateChanged(firebaseAuth, async (newUser) => {
      user.value = newUser && markRaw(newUser)

      if (newUser)
        await reloadToken()

      userLoggedIn.resolve()

      if (newUser)
        await associateUser()
    })
  }

  /**
   * Reload the user's ID token
   */
  async function reloadToken() {
    if (!user.value) {
      throw new Error('Cannot reload token when you are not logged in.')
    }

    idToken.value = await user.value.getIdTokenResult()

    return idToken.value
  }

  /**
   * Start login process
   * @param options Options for the login process
   */
  async function login(options?: { redirect?: string }) {
    const provider = new GoogleAuthProvider()
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    userLoggedIn = Promise.withResolvers<void>()

    const result = await signInWithPopup(firebaseAuth, provider)

    if (!result.user) {
      throw new Error('No user found')
    }

    user.value = markRaw(result.user)

    if (options?.redirect) {
      void navigateTo({
        path: options.redirect,
        replace: true,
      })
    }
  }

  /**
   * Logout the user
   * @param options Options for the logout process
   */
  async function logout(options?: { redirect?: boolean }) {
    if (options?.redirect) {
      await navigateTo({
        path: '/login',
        replace: true,
      })
    }

    await firebaseAuth?.signOut()

    user.value = null
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    userLoggedIn = Promise.withResolvers<void>()
  }

  /**
   * Get the user's ID token
   * @returns The user's ID token
   */
  async function getIdToken() {
    await userLoggedIn.promise

    await reloadToken()

    return idToken.value?.token
  }

  /**
   * Wait for the user to log in
   */
  async function waitForUserLogin() {
    await userLoggedIn.promise

    return isLoggedIn.value
  }

  async function associateUser() {
    await gatewayFetch('/authenticate-user')
  }

  initialize()

  return {
    user,
    idToken,
    waitForUserLogin,
    isLoggedIn,
    getIdToken,
    login,
    logout,
  }
}
